import React, { FC, useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import ProjectPageShell, { Crumb } from "../shared/ProjectPageShell";
import TimeLogInformationById from "./TimeLogInformationById";
import TimeLogQuickViewCard from "./TimeLogQuickViewCard";
import { RootState } from "../../../store/store";
import {
  updateTimeLogInDetailModalSlice,
  resetTimeLogInDetailModalSlice,
} from "../../../store/slices/projectManagement/time-log-view/TimeLogDetailView";
import { logHoursApis } from "../../../api/TimeLog";

// Helper functions
const secondsToHHMM = (totalSeconds: number) => {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const to12HourTime = (time?: string): string => {
  if (!time || !time.includes(":")) return "--:--";
  const [h, m] = time.split(":");
  let hour = parseInt(h, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12;
  return `${String(hour).padStart(2, "0")}:${m} ${ampm}`;
};

const TimeLogDetailPage: FC = () => {
  const { timeLogId } = useParams<{ timeLogId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [timeLogData, setTimeLogData] = useState<any>(null);
  const [viewMode, setViewMode] = useState<"summary" | "edit">("summary");

  useEffect(() => {
    setViewMode("summary");
  }, [timeLogId]);

  useEffect(() => {
    return () => {
      dispatch(resetTimeLogInDetailModalSlice());
    };
  }, [dispatch]);

  // Get data from location state
  const locationState = location.state as any;
  const projectId = locationState?.projectId || locationState?.id;
  const projectName = locationState?.projectName;
  const taskId = locationState?.taskId;
  const taskName = locationState?.taskName;
  const issueId = locationState?.issueId;
  const issueName = locationState?.issueName;
  // Generic return path - allows navigation back to any source page (e.g., notifications)
  const returnPath = locationState?.returnPath;
  const returnState = locationState?.returnState;

  const { data: TimeLogInDetailModal } = useSelector(
    (state: RootState) => state.TimeLogListDetailModal
  );

  // Get projectId from Redux layout state as fallback
  const { currentProjectId, currentProjectName } = useSelector(
    (state: RootState) => state.layout
  );

  // Use Redux projectId as fallback when location state doesn't have it
  const resolvedProjectId = projectId || currentProjectId;
  const resolvedProjectName = projectName || currentProjectName;

  // Fetch time log details
  useEffect(() => {
    const fetchTimeLogDetails = async () => {
      if (!timeLogId) return;

      setLoading(true);
      try {
        // Fetch time log data
        const listResponse = await logHoursApis.listTimeLog({
          pageSize: 10,
          direction: "P",
          sortOrder: "DESC",
          pageDiff: 0,
          paginationId: null,
          projectIds: resolvedProjectId,
          taskIds: taskId || undefined,
          issueIds: issueId || undefined,
        });

        if (listResponse?.status && listResponse?.data?.list) {
          // Find the time log in the list
          const timeLog = listResponse.data.list.find(
            (log: any) => log.timeLogId === Number(timeLogId)
          );

          if (timeLog) {
            setTimeLogData(timeLog);

            // Filter timeLogResources for this specific time log
            const filteredTimeLogResources =
              timeLog.timeLogResources?.filter(
                (resource: any) => resource.timeLogId === timeLog.timeLogId
              ) || [];

            // Create selected log object
            const selectedLog = {
              timeLogId: timeLog.timeLogId,
              timeLogTitle: `Time Log #${timeLog.timeLogId}`,
              projectId: timeLog.projectId,
              projectTitle: timeLog.projectName || resolvedProjectName || `Project #${timeLog.projectId}`,
              user: timeLog.createdByName || "—",
              dailyLogHours: secondsToHHMM(timeLog.totalWorkHoursInSec || 0),
              timePeriod: `${to12HourTime(timeLog.loginTime)} - ${to12HourTime(timeLog.logoutTime)}`,
              billingType: timeLog.billingType || "—",
              notes: timeLog.notes || "—",
              createdBy: timeLog.createdBy || "—",
              createdAt: timeLog.createdAt || "—",
              ...timeLog,
              timeLogResources: filteredTimeLogResources,
              totalWorkHoursInSec: timeLog.totalWorkHoursInSec || 0,
              loginTime: timeLog.loginTime,
              logoutTime: timeLog.logoutTime,
              logDate: timeLog.logDate,
              orgId: timeLog.orgId,
              taskId: timeLog.taskId,
              taskName: timeLog.taskName,
              userId: timeLog.userId,
            };

            // Update Redux state for child components. The slice is shared
            // with TimeLogInformationById's edit-mode flow.
            dispatch(
              updateTimeLogInDetailModalSlice({
                open: false,
                id: timeLog.timeLogId,
                projectId: timeLog.projectId || resolvedProjectId,
                projectName: timeLog.projectName || resolvedProjectName,
                taskId: timeLog.taskId || taskId,
                taskName: timeLog.taskName || taskName,
                issueId: timeLog.issueId || issueId,
                issueName: timeLog.issueName || issueName,
                selectedTimeLogDetails: selectedLog,
                activeTab: "details",
                extra: {
                  createdAt: timeLog.createdAt,
                  totalWorkHoursInSec: timeLog.totalWorkHoursInSec,
                  billingType: timeLog.billingType,
                  dailyLogHours: secondsToHHMM(timeLog.totalWorkHoursInSec || 0),
                  timePeriod: `${to12HourTime(timeLog.loginTime)} - ${to12HourTime(timeLog.logoutTime)}`,
                  notes: timeLog.notes,
                  shiftName: timeLog.shiftName,
                  approvalStatus: timeLog.approvalStatus,
                },
              })
            );
          }
        }
      } catch (error) {
        console.error("Error fetching time log details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTimeLogDetails();
  }, [timeLogId, dispatch, resolvedProjectId, resolvedProjectName, taskId, taskName, issueId, issueName]);

  // Navigation handlers
  const handleBack = () => {
    dispatch(resetTimeLogInDetailModalSlice());
    // Use returnPath if available (e.g., from notifications)
    if (returnPath) {
      navigate(returnPath, { state: returnState });
    } else {
      navigate(`/pms/project/timesheet/${resolvedProjectId}`, {
        state: {
          ...locationState,
          id: resolvedProjectId,
          projectName: resolvedProjectName,
        },
      });
    }
  };

  const handleBackToTimeSheets = () => {
    dispatch(resetTimeLogInDetailModalSlice());
    navigate(`/pms/project/timesheet/${resolvedProjectId}`, {
      state: {
        ...locationState,
        id: resolvedProjectId,
        projectName: resolvedProjectName,
      },
    });
  };

  const handleBackToProject = () => {
    dispatch(resetTimeLogInDetailModalSlice());
    navigate(`/pms/project/dashboard/${resolvedProjectId}`, { state: locationState });
  };

  const handleClose = () => {
    handleBackToTimeSheets();
  };

  // Refresh data function
  const refreshData = async () => {
    if (!timeLogId || !resolvedProjectId) return;

    try {
      const listResponse = await logHoursApis.listTimeLog({
        pageSize: 10,
        direction: "P",
        sortOrder: "DESC",
        pageDiff: 0,
        paginationId: null,
        projectIds: resolvedProjectId,
        taskIds: taskId || undefined,
        issueIds: issueId || undefined,
      });

      if (listResponse?.status && listResponse?.data?.list) {
        const timeLog = listResponse.data.list.find(
          (log: any) => log.timeLogId === Number(timeLogId)
        );

        if (timeLog) {
          setTimeLogData(timeLog);

          const filteredTimeLogResources =
            timeLog.timeLogResources?.filter(
              (resource: any) => resource.timeLogId === timeLog.timeLogId
            ) || [];

          const selectedLog = {
            timeLogId: timeLog.timeLogId,
            timeLogTitle: `Time Log #${timeLog.timeLogId}`,
            projectId: timeLog.projectId,
            projectTitle: timeLog.projectName || resolvedProjectName || `Project #${timeLog.projectId}`,
            user: timeLog.createdByName || "—",
            dailyLogHours: secondsToHHMM(timeLog.totalWorkHoursInSec || 0),
            timePeriod: `${to12HourTime(timeLog.loginTime)} - ${to12HourTime(timeLog.logoutTime)}`,
            billingType: timeLog.billingType || "—",
            notes: timeLog.notes || "—",
            createdBy: timeLog.createdBy || "—",
            createdAt: timeLog.createdAt || "—",
            ...timeLog,
            timeLogResources: filteredTimeLogResources,
            totalWorkHoursInSec: timeLog.totalWorkHoursInSec || 0,
            loginTime: timeLog.loginTime,
            logoutTime: timeLog.logoutTime,
            logDate: timeLog.logDate,
            orgId: timeLog.orgId,
            taskId: timeLog.taskId,
            taskName: timeLog.taskName,
            userId: timeLog.userId,
          };

          dispatch(
            updateTimeLogInDetailModalSlice({
              selectedTimeLogDetails: selectedLog,
              extra: {
                createdAt: timeLog.createdAt,
                totalWorkHoursInSec: timeLog.totalWorkHoursInSec,
                billingType: timeLog.billingType,
                dailyLogHours: secondsToHHMM(timeLog.totalWorkHoursInSec || 0),
                timePeriod: `${to12HourTime(timeLog.loginTime)} - ${to12HourTime(timeLog.logoutTime)}`,
                notes: timeLog.notes,
                shiftName: timeLog.shiftName,
                approvalStatus: timeLog.approvalStatus,
              },
            })
          );
        }
      }
    } catch (error) {
      console.error("Error refreshing time log data:", error);
    }
  };

  const getTimeLogTitle = () => {
    if (timeLogData?.taskName?.trim?.()) return timeLogData.taskName;
    if (timeLogData?.issueName?.trim?.()) return timeLogData.issueName;
    return `Time Log #${timeLogId}`;
  };

  const logTitle = getTimeLogTitle();

  const breadcrumb: Crumb[] = [
    { label: resolvedProjectName || "Project", onClick: handleBackToProject },
    { label: "Time Sheets", onClick: handleBackToTimeSheets },
    { label: logTitle },
  ];

  return (
    <ProjectPageShell
      headTitle={`${logTitle} - Time Log Details`}
      breadcrumb={breadcrumb}
      title={logTitle}
      eyebrow="Time Log"
      onBack={handleBack}
      loading={loading}
    >
      {viewMode === "summary" ? (
        <TimeLogQuickViewCard
          timeLogData={
            TimeLogInDetailModal?.selectedTimeLogDetails || timeLogData
          }
          onEdit={() => setViewMode("edit")}
          onRefresh={refreshData}
        />
      ) : (
        <TimeLogInformationById
          refreshData={TimeLogInDetailModal?.selectedTimeLogDetails}
          isFromWorkUI={false}
          initialEditMode={true}
          onBackToSummary={() => setViewMode("summary")}
          refreshTimeLogList={() => {
            refreshData();
            setViewMode("summary");
          }}
        />
      )}
    </ProjectPageShell>
  );
};

export default TimeLogDetailPage;
